<template>
  <v-container>
    <v-layout>
      <v-flex
        xs12
        sm8
        offset-sm2
      >
        <div class="lodging-page iabf-dancer-theme">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>

          <h1>{{ isFaculty ? 'Travel &amp; carpool' : 'Travel &amp; lodging' }}</h1>
          <p class="page-subtitle">
            {{ isFaculty
              ? 'Flights or drive times, then a free airport carpool if you’d like one.'
              : 'Four steps: airports, your room, trip times, and a ride if you want one.' }}
          </p>

          <div class="lodging-steps">
          <section class="your-lodging iabf-card">
            <button
              type="button"
              class="your-lodging-header"
              :aria-expanded="showTravelInfo ? 'true' : 'false'"
              @click="showTravelInfo = !showTravelInfo"
            >
              <div class="your-lodging-header-text">
                <p class="your-lodging-kicker">Step 1</p>
                <h2 class="your-lodging-title">Plan your Travel</h2>
                <p class="your-lodging-collapsed-hint">
                  Which airport to book, parking information, and when to arrive.
                </p>
              </div>
              <v-icon class="your-lodging-chevron">
                {{ showTravelInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>
            <div v-show="showTravelInfo" class="your-lodging-body">
              <p><strong>Festival days</strong></p>
              <ul>
                <li>
                  <strong>{{ festival.arrivalWeekdayLabel }} — Arrival day:</strong>
                  <template v-if="isOffsite">4:00 Festival Check-in, 6:00 Welcome Party</template>
                  <template v-else>
                    {{ festival.checkInTimeLabel }} check-in,
                    {{ festival.welcomeTimeLabel }} welcome party
                  </template>
                </li>
                <li>
                  <strong>{{ festival.departureWeekdayLabel }} — Departure day:</strong>
                  <template v-if="isOffsite">Breakfast {{ festival.breakfastTimeLabel }}</template>
                  <template v-else>
                    {{ festival.breakfastTimeLabel }} breakfast,
                    {{ festival.checkoutTimeLabel }} checkout
                  </template>
                </li>
              </ul>
              <p><strong>Parking</strong></p>
              <p>Your ticket includes complimentary self-parking onsite.</p>
              <p><strong>Airports</strong></p>
              <p>IABF organizes airport carpools to the venue to cut down on costs &amp; meet friends along the way.</p>
              <ul>
                <li><strong>Orlando (MCO)</strong> — Closest major airport. Approx 75 min drive.</li>
                <li><strong>Daytona (DAB)</strong> — Closest regional airport. Approx 30 min drive.</li>
                <li><strong>Sanford (SFB)</strong> — Orlando regional airport. Approx 1 hour drive.</li>
              </ul>
            </div>
          </section>

          <section v-if="isFaculty && isOffsite" class="your-lodging iabf-card faculty-stay-card">
            <p class="your-lodging-kicker">Your stay</p>
            <h2 class="your-lodging-title">SpringHill Suites New Smyrna Beach</h2>
            <p class="your-lodging-lede">
              You’re staying at SpringHill Suites — we’ll take care of the room.
              Address: 512 Flagler Ave, New Smyrna Beach. That’s also where the festival shuttle picks up.
            </p>
          </section>

          <!-- ═══ STEP 2: room / offsite hotels ═══ -->
          <section v-if="!isFaculty" class="your-lodging iabf-card">
            <button
              type="button"
              class="your-lodging-header"
              :aria-expanded="showLodgingInfo ? 'true' : 'false'"
              @click="showLodgingInfo = !showLodgingInfo"
            >
              <div class="your-lodging-header-text">
                <p class="your-lodging-kicker">Step 2</p>
                <h2 class="your-lodging-title">{{ lodgingAccordionTitle }}</h2>
                <p class="your-lodging-collapsed-hint">
                  {{ lodgingAccordionHint }}
                  <span v-if="hasPendingExtraNights" class="lodging-pending-pill">Extra nights requested</span>
                  <span
                    v-else-if="hasConfirmedExtraNights"
                    class="lodging-pending-pill lodging-pending-pill--confirmed"
                  >Extra nights confirmed</span>
                </p>
              </div>
              <v-icon class="your-lodging-chevron">
                {{ showLodgingInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>

            <div v-show="showLodgingInfo" class="your-lodging-body">
              <p class="your-lodging-lede">{{ lodgingSummary }}</p>

              <div class="lodging-facts">
                <div v-if="assignedRoom" class="lodging-fact">
                  <span class="lodging-fact-label">Your room</span>
                  <span class="lodging-fact-value">Room {{ assignedRoom }}</span>
                </div>
                <div class="lodging-fact">
                  <span class="lodging-fact-label">Festival nights</span>
                  <span v-if="isOffsite" class="lodging-fact-value lodging-fact-value--stack">
                    <span>{{ festival.arrivalWeekdayLabel }} — 4:00 Festival Check-in, 6:00 Welcome Party</span>
                    <span>{{ festival.departureWeekdayLabel }} — Breakfast {{ festival.breakfastTimeLabel }}</span>
                  </span>
                  <span v-else class="lodging-fact-value">
                    {{ festival.arrivalWeekdayLabel }} (check-in {{ festival.checkInTimeLabel }})
                    →
                    {{ festival.departureWeekdayLabel }} (checkout {{ festival.checkoutTimeLabel }})
                  </span>
                </div>
                <div v-if="packageName" class="lodging-fact">
                  <span class="lodging-fact-label">Package</span>
                  <span class="lodging-fact-value">
                    {{ packageName }}
                    <router-link to="/registration" class="lodging-package-link">What’s included →</router-link>
                  </span>
                </div>
                <div v-if="hasAnyExtraNightOnFile" class="lodging-fact">
                  <span class="lodging-fact-label">Extra nights</span>
                  <span class="lodging-fact-value">
                    <span
                      v-if="hasPendingExtraNights"
                      class="lodging-pending-pill lodging-pending-pill--inline"
                    >Requested — pending approval</span>
                    <span
                      v-else-if="hasConfirmedExtraNights"
                      class="lodging-pending-pill lodging-pending-pill--confirmed lodging-pending-pill--inline"
                    >Confirmed</span>
                    <ul class="extra-nights-fact-list">
                      <li v-for="night in onFileExtraNightRows" :key="night.key">
                        {{ night.label }}
                        <span class="extra-night-badge" :class="'badge-' + night.status">
                          {{ nightStatusLabel(night.status) }}
                        </span>
                      </li>
                    </ul>
                  </span>
                </div>
              </div>

              <div v-if="roomAmenities" class="lodging-amenities">
                <h3 class="lodging-amenities-title">{{ roomAmenities.title }}</h3>
                <ul class="lodging-amenities-list">
                  <li v-for="(item, idx) in roomAmenities.items" :key="idx">
                    {{ item.label || item }}
                  </li>
                </ul>
              </div>

              <!-- Onsite + roommate: hairdryer preference -->
              <div v-if="isOnsiteStay" class="hairdryer-block">
                <h3 class="extra-nights-heading">Hairdryer in your room?</h3>
                <p class="extra-nights-copy">
                  We can place a hairdryer in onsite rooms (including room-shares).
                  Let us know if you’d like one.
                </p>
                <div class="hairdryer-choices" role="group" aria-label="Hairdryer preference">
                  <button
                    type="button"
                    class="hairdryer-choice"
                    :class="{ 'is-selected': wantsHairdryer === true }"
                    :disabled="hairdryerSaving"
                    @click="saveHairdryerPreference(true)"
                  >
                    Yes, please
                  </button>
                  <button
                    type="button"
                    class="hairdryer-choice"
                    :class="{ 'is-selected': wantsHairdryer === false }"
                    :disabled="hairdryerSaving"
                    @click="saveHairdryerPreference(false)"
                  >
                    No thanks
                  </button>
                </div>
                <p v-if="hairdryerError" class="form-error" role="alert">{{ hairdryerError }}</p>
                <p v-if="hairdryerSuccess" class="form-success" role="status">{{ hairdryerSuccess }}</p>
              </div>

              <!-- Onsite: coffee maker stock (same fields as dietary, stay on this page) -->
              <div v-if="isOnsiteStay" class="coffee-block">
                <h3 class="extra-nights-heading">Stock your coffee maker?</h3>
                <template v-if="showCoffeeMakerSummary">
                  <p class="coffee-summary">{{ coffeeMakerSummaryLine }}</p>
                  <button
                    type="button"
                    class="iabf-btn iabf-btn--secondary coffee-update"
                    @click="coffeeMakerEditing = true"
                  >
                    Update
                  </button>
                </template>
                <template v-else>
                  <p class="extra-nights-copy">
                    What would you like us to stock it with?
                  </p>
                  <coffee-maker-fields
                    v-model="coffeeMakerStock"
                    compact
                  />
                  <p v-if="coffeeMakerError" class="form-error" role="alert">{{ coffeeMakerError }}</p>
                  <p v-if="coffeeMakerSuccess" class="form-success" role="status">{{ coffeeMakerSuccess }}</p>
                  <div class="coffee-actions">
                    <button
                      type="button"
                      class="iabf-btn iabf-btn--primary"
                      :disabled="coffeeMakerSaving"
                      @click="saveCoffeeMakerStock"
                    >
                      {{ coffeeMakerSaving ? 'Saving…' : 'Save' }}
                    </button>
                    <button
                      v-if="coffeeMakerEntered"
                      type="button"
                      class="iabf-btn iabf-btn--ghost"
                      :disabled="coffeeMakerSaving"
                      @click="coffeeMakerEditing = false"
                    >
                      Cancel
                    </button>
                  </div>
                </template>
              </div>

              <!-- Onsite: extra nights with clear requested / confirmed status -->
              <template v-if="isOnsiteStay">
                <div class="extra-nights-block">
                  <h3 class="extra-nights-heading">Need a night before or after?</h3>
                  <p class="extra-nights-copy">
                    Extra nights at the venue are
                    <strong>${{ roomRate }}/night</strong> (taxes included),
                    <strong>by request if space is available</strong>.
                    Select new nights below, then submit — requested nights stay on file until we confirm.
                  </p>

                  <div v-if="hasAnyExtraNightOnFile" class="extra-nights-status-banner">
                    <strong v-if="hasPendingExtraNights">Request received — pending approval</strong>
                    <strong v-else-if="hasConfirmedExtraNights">Extra nights confirmed</strong>
                    <p v-if="extraNightPaymentMethod">
                      Payment preference:
                      {{ extraNightPaymentMethod === 'charge' ? 'charge card on file when confirmed' : 'invoice' }}.
                    </p>
                    <p v-if="extraNightRemainingBalance > 0">
                      Remaining balance due: <strong>${{ extraNightRemainingBalance }}</strong>
                    </p>
                  </div>

                  <ul class="extra-night-list">
                    <li
                      v-for="night in extraNightRows"
                      :key="night.key"
                      class="extra-night-row"
                      :class="{
                        'is-requested': night.status === 'requested',
                        'is-confirmed': night.status === 'confirmed' || night.status === 'paid',
                        'is-picking': night.picking
                      }"
                    >
                      <label v-if="!night.status" class="extra-night-pick">
                        <input
                          type="checkbox"
                          :checked="night.picking"
                          @change="toggleExtraNightPick(night.key, $event.target.checked)"
                        >
                        <span class="extra-night-label">{{ night.label }}</span>
                        <span class="extra-night-hint">if available</span>
                      </label>
                      <div v-else class="extra-night-status-row">
                        <span class="extra-night-label">{{ night.label }}</span>
                        <span
                          class="extra-night-badge"
                          :class="'badge-' + night.status"
                        >{{ nightStatusLabel(night.status) }}</span>
                      </div>
                    </li>
                  </ul>

                  <div v-if="newPickCount > 0" class="extra-nights-pay">
                    <p class="extra-nights-total">
                      <strong>New nights selected: ${{ newPickCount * roomRate }}</strong>
                      <span v-if="onFileNightCount">
                        · already on file: ${{ onFileNightCount * roomRate }}
                      </span>
                    </p>
                    <p class="extra-nights-copy">How would you like to pay for extra nights?</p>
                    <v-radio-group v-model="extraNightPayment" class="extra-night-radios">
                      <v-radio
                        label="Charge the card on file when the venue confirms (within 48 hours)"
                        value="charge"
                      />
                      <v-radio
                        label="Send me an invoice (small fee may apply)"
                        value="invoice"
                      />
                    </v-radio-group>
                    <div class="lodging-callout lodging-callout--soft meals-in-total">
                      <strong>Meals at the venue</strong>
                      <p>
                        The Artist’s Retreat only has food service when a festival group is on campus.
                        If you arrive early or stay late, plan to go offsite for groceries or restaurants.
                      </p>
                    </div>
                    <p class="extra-nights-fine">
                      Cancel extra nights for a full refund until May 6 by emailing
                      <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
                    </p>
                    <p v-if="extraNightsError" class="form-error" role="alert">{{ extraNightsError }}</p>
                    <p v-if="extraNightsSuccess" class="form-success" role="status">{{ extraNightsSuccess }}</p>
                    <button
                      type="button"
                      class="iabf-btn iabf-btn--primary extra-nights-save"
                      :disabled="extraNightsSaving"
                      @click="submitExtraNightsRequest"
                    >
                      {{ extraNightsSaving ? 'Sending request…' : 'Request selected nights' }}
                    </button>
                  </div>
                  <template v-else>
                    <p v-if="extraNightsError" class="form-error" role="alert">{{ extraNightsError }}</p>
                    <p v-if="extraNightsSuccess" class="form-success" role="status">{{ extraNightsSuccess }}</p>
                    <p v-if="!hasAnyExtraNightOnFile" class="extra-nights-fine">
                      No extra nights on file. Select nights above to request them, or email
                      <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
                    </p>
                    <p v-else class="extra-nights-fine">
                      To change or cancel a requested night, email
                      <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
                    </p>
                  </template>
                </div>
              </template>

              <!-- Offsite: hotels + shuttle -->
              <template v-else-if="isOffsite">
                <div class="lodging-callout">
                  <strong>Book your own beach lodging</strong>
                  <p>
                    Festival Access doesn’t include a room — you’ll book lodging yourself.
                    Hotels fill early for next summer. A few we like:
                  </p>
                  <ul class="hotel-list">
                    <li>
                      <strong>SpringHill Suites New Smyrna Beach</strong>
                      — offsite shuttle pickup location
                      (512 Flagler Ave, New Smyrna Beach, FL 32169)
                    </li>
                    <li><strong>Seahorse Inn</strong></li>
                    <li><strong>Salty Mermaid</strong></li>
                  </ul>
                </div>
                <template v-if="hasShuttle">
                  <div class="lodging-callout lodging-callout--soft">
                    <strong>Shuttle included with your registration</strong>
                    <ul class="hotel-list">
                      <li>Morning pickup: 7:30 AM</li>
                      <li>Evening pickup: 7:30 PM</li>
                      <li>Meeting point: SpringHill Suites, 512 Flagler Ave</li>
                      <li>~15 minutes to the venue</li>
                    </ul>
                  </div>
                </template>
                <template v-else>
                  <p class="your-lodging-lede">
                    Your ticket doesn’t include the shuttle — plan a rideshare or drive.
                    Complimentary self-parking is included onsite.
                  </p>
                </template>
              </template>

              <!-- No room on file yet -->
              <template v-else>
                <p class="your-lodging-lede">
                  We don’t have a lodging type on file yet.
                  Check
                  <router-link to="/registration">your package</router-link>
                  or email
                  <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
                  if something looks wrong.
                </p>
              </template>
            </div>
          </section>

          <section class="your-lodging iabf-card">
            <button
              type="button"
              class="your-lodging-header"
              :aria-expanded="showTripInfo ? 'true' : 'false'"
              @click="showTripInfo = !showTripInfo"
            >
              <div class="your-lodging-header-text">
                <p class="your-lodging-kicker">{{ isFaculty ? 'Step 2' : 'Step 3' }}</p>
                <h2 class="your-lodging-title">Arrival &amp; Departure</h2>
                <p class="your-lodging-collapsed-hint">{{ tripAccordionHint }}</p>
              </div>
              <v-icon class="your-lodging-chevron">
                {{ showTripInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>
            <div v-show="showTripInfo" class="your-lodging-body">
              <div v-show="travelDetailsEntered" class="trip-summary">
                <h3 class="extra-nights-heading">Your arrival</h3>
                <div v-if="arrivalType" class="trip-card">
                  <p v-if="arrivalType === 'Plane'" class="trip-card-body">
                    {{ formatTripDate(arrivalDate) }} around {{ arrivalTime }} ET<br />
                    {{ arrivalAirport }} · flight {{ arrivalFlightNumber }}
                  </p>
                  <p v-else class="trip-card-body">
                    Driving in {{ formatTripDate(arrivalDate) }} around {{ arrivalTime }} ET<br />
                    <span class="trip-card-note">Your ticket includes complimentary self-parking onsite</span>
                  </p>
                </div>

                <h3 class="extra-nights-heading trip-subheading">Your departure</h3>
                <div v-if="departureType" class="trip-card">
                  <p v-if="departureType === 'Plane'" class="trip-card-body">
                    {{ formatTripDate(departureDate) }} around {{ departureTime }} ET<br />
                    {{ departureAirport }} · flight {{ departureFlightNumber }}
                  </p>
                  <p v-else class="trip-card-body">
                    Driving out {{ formatTripDate(departureDate) }} around {{ departureTime }} ET
                  </p>
                </div>
                <div v-if="arrivalType === 'Plane'" class="carpool-cta">
                  <p>✈️ Flying in? You might share a ride with other dancers.</p>
                  <button
                    type="button"
                    class="iabf-btn iabf-btn--primary carpool-jump"
                    @click="openCarpoolStep"
                  >
                    Choose a carpool
                  </button>
                </div>
                <v-btn small outlined class="mt-3" @click="travelDetailsEntered = false; showTripInfo = true">Update trip details</v-btn>
              </div>

              <form
                v-show="!travelDetailsEntered"
                @submit.prevent="submitTravelDetails"
              >
            <v-flex>
              <h3 class="extra-nights-heading">Your arrival</h3>
              <p class="trip-question">How are you arriving?</p>
              <div class="iabf-chips trip-pills">
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': arrivalType === 'Plane' }"
                  @click="arrivalType = 'Plane'"
                >
                  Plane
                </button>
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': arrivalType === 'Car' }"
                  @click="arrivalType = 'Car'"
                >
                  Car
                </button>
              </div>
              <span v-show="arrivalType === 'Car'">
                <p><i>Nice — your ticket includes complimentary self-parking onsite.</i></p>
              </span>
              <span v-show="arrivalType === 'Plane'">
                <p class="trip-question">Which airport?</p>
                <div class="iabf-chips trip-pills">
                  <button
                    v-for="opt in airportOptions"
                    :key="'arr-' + opt"
                    type="button"
                    class="iabf-chip"
                    :class="{ 'is-selected': arrivalAirport === opt }"
                    @click="arrivalAirport = opt"
                  >
                    {{ opt }}
                  </button>
                </div>
                <v-text-field
                  v-show="arrivalAirport === 'Other'"
                  v-model="arrivalAirportOther"
                  label="Which airport are you flying into?"
                ></v-text-field>
                <v-text-field
                  v-model="arrivalFlightNumber"
                  label="Arrival flight number"
                  :required="arrivalType === 'Plane'"
                ></v-text-field>
              </span>
              <p class="trip-question">Arrival date</p>
              <v-date-picker
                v-model="arrivalDate"
                color="#8a957d"
                header-color="#8a957d"
                label="Arrival Date"
                required
                class="iabf-date-picker"
              ></v-date-picker>
              <v-text-field
                v-model="arrivalTime"
                label="About what time? (Eastern)"
                required
              ></v-text-field>

              <h3 class="extra-nights-heading trip-subheading">Your departure</h3>
              <p class="trip-question">How are you heading home?</p>
              <div class="iabf-chips trip-pills">
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': departureType === 'Plane' }"
                  @click="departureType = 'Plane'"
                >
                  Plane
                </button>
                <button
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': departureType === 'Car' }"
                  @click="departureType = 'Car'"
                >
                  Car
                </button>
              </div>
              <span v-show="departureType === 'Plane'">
              <p class="trip-question">Which airport home?</p>
              <div class="iabf-chips trip-pills">
                <button
                  v-for="opt in airportOptions"
                  :key="'dep-' + opt"
                  type="button"
                  class="iabf-chip"
                  :class="{ 'is-selected': departureAirport === opt }"
                  @click="departureAirport = opt"
                >
                  {{ opt }}
                </button>
              </div>
              <v-text-field
                v-show="departureAirport === 'Other'"
                v-model="departureAirportOther"
                label="Which airport are you leaving from?"
              ></v-text-field>
              <v-text-field
                v-model="departureFlightNumber"
                label="Departure flight number"
                :required="departureType === 'Plane'"
              ></v-text-field>
              </span>
              <p class="trip-question">Departure date</p>
              <v-date-picker
                v-model="departureDate"
                color="#8a957d"
                header-color="#8a957d"
                label="Departure Date"
                required
                class="iabf-date-picker"
              ></v-date-picker>
              <v-text-field
                v-model="departureTime"
                label="About what time? (Eastern)"
                required
              ></v-text-field>
            </v-flex>
            <p v-if="travelError" class="form-error" role="alert">{{ travelError }}</p>
            <p v-if="travelSuccess" class="form-success" role="status">{{ travelSuccess }}</p>
            <v-btn type="submit" color="primary" dark :loading="travelSaving" :disabled="travelSaving">
              {{ travelSaving ? 'Saving…' : 'Save trip details' }}
            </v-btn>
              </form>
            </div>
          </section>

          <section id="carpool" class="your-lodging iabf-card">
            <button
              type="button"
              class="your-lodging-header"
              :aria-expanded="showCarpoolInfo ? 'true' : 'false'"
              @click="showCarpoolInfo = !showCarpoolInfo"
            >
              <div class="your-lodging-header-text">
                <p class="your-lodging-kicker">{{ isFaculty ? 'Step 3' : 'Step 4' }}</p>
                <h2 class="your-lodging-title">Airport Carpool</h2>
                <p class="your-lodging-collapsed-hint">
                  Share a ride with other dancers — pick your times here.
                </p>
              </div>
              <v-icon class="your-lodging-chevron">
                {{ showCarpoolInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>
            <div v-show="showCarpoolInfo" class="your-lodging-body">
              <AirportCarpools embedded />
            </div>
          </section>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
  import {
    computeCheckIn,
    computeCheckOut,
    getDefaultCheckIn,
    getDefaultCheckOut,
    extraNightIsoWindows
  } from '@/utils/lodgingDates'
  import { festivalWeek } from '@/config/iabf-festival-dates'
  import { IABF_DANCER_YEAR } from '@/config/iabf-event'
  import { saveErrorMessage } from '@/utils/save-error'
  import {
    coffeeMakerSummary,
    hasCoffeeMakerPicks
  } from '@/config/iabf-dietary'
  import {
    assignedRoomNumber,
    lodgingLabelFromRoom,
    packageKeyFromRoom,
    packageNameFromRoom,
    roomAmenitiesForRoom
  } from '@/utils/iabf-registration-package'
  import AirportCarpools from '@/views/IABFDashboard/Carpools.vue'
  import CoffeeMakerFields from '@/components/iabf/CoffeeMakerFields.vue'

  export default {
    components: {
      AirportCarpools,
      CoffeeMakerFields
    },
    data () {
      const fest = festivalWeek(IABF_DANCER_YEAR)
      return {
        defaultCheckIn: getDefaultCheckIn(IABF_DANCER_YEAR),
        defaultCheckOut: getDefaultCheckOut(IABF_DANCER_YEAR),
        airportOptions: [
          'Orlando (MCO)',
          'Daytona Beach (DAB)',
          'Sanford (SFB)',
          'Other'
        ],
        roomRate: 150,
        firstName: '',
        lastName: '',
        room: '',
        primaryRoomId: '',
        arrivalType: '',
        arrivalAirport: '',
        arrivalAirportOther: '',
        arrivalFlightNumber: '',
        // Picker defaults only — not a saved trip until travelDetailsEntered
        arrivalDate: fest.arrivalIso,
        arrivalTime: '',
        departureType: '',
        departureAirport: '',
        departureAirportOther: '',
        departureFlightNumber: '',
        departureDate: fest.departureIso,
        departureTime: '',
        // Status strings from travel-details: '', 'requested', 'confirmed', 'paid'
        // plus local picks: true (about to request)
        extraNight1: false,
        extraNight2: false,
        extraNight3: false,
        extraNight4: false,
        extraNight5: false,
        extraNight6: false,
        extraNightPayment: '',
        arrivalSharedCar: false,
        departureSharedCar: false,
        travelDetailsEntered: false,
        travelDetailsId: null,
        showTravelInfo: false,
        showLodgingInfo: false,
        showTripInfo: false,
        showCarpoolInfo: false,
        checkIn: '',
        checkOut: '',
        extraNightsTotal: 0,
        extraNightRemainingBalance: 0,
        extraNightPaymentMethod: '',
        extraNightInvoiceLink: '',
        travelError: '',
        travelSuccess: '',
        travelSaving: false,
        extraNightsError: '',
        extraNightsSuccess: '',
        extraNightsSaving: false,
        wantsHairdryer: null,
        hairdryerSaving: false,
        hairdryerError: '',
        hairdryerSuccess: '',
        coffeeMakerStock: [],
        coffeeMakerEntered: false,
        coffeeMakerEditing: false,
        coffeeMakerSaving: false,
        coffeeMakerError: '',
        coffeeMakerSuccess: ''
      }
    },
    computed: {
      isFaculty () {
        const d = this.currentIABFDancer || {}
        return !!(d.isFaculty || d.personType === 'faculty')
      },
      extraNightKeys () {
        return ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']
      },
      extraNightRows () {
        const labels = this.extraNights.labels || {}
        const labelMap = {
          extraNight1: labels.n1,
          extraNight2: labels.n2,
          extraNight3: labels.n3,
          extraNight4: labels.n4,
          extraNight5: labels.n5,
          extraNight6: labels.n6
        }
        return this.extraNightKeys.map(key => {
          const raw = this[key]
          const status = this.normalizeExtraNightStatus(raw)
          return {
            key,
            label: labelMap[key] || key,
            status, // '', 'requested', 'confirmed', 'paid'
            picking: raw === true // newly checked for this request
          }
        })
      },
      roomTotal () {
        return this.extraNightRows.filter(n => n.status || n.picking).length * this.roomRate
      },
      newPickCount () {
        return this.extraNightRows.filter(n => n.picking).length
      },
      onFileNightCount () {
        return this.extraNightRows.filter(n => !!n.status).length
      },
      hasAnyExtraNightOnFile () {
        return this.onFileNightCount > 0
      },
      hasPendingExtraNights () {
        return this.extraNightRows.some(n => n.status === 'requested')
      },
      hasConfirmedExtraNights () {
        return this.extraNightRows.some(n => n.status === 'confirmed' || n.status === 'paid')
      },
      onFileExtraNightRows () {
        return this.extraNightRows.filter(n => !!n.status)
      },
      selectedExtraNightLabels () {
        return this.extraNightRows
          .filter(n => n.picking || n.status)
          .map(n => n.label)
      },
      currentIABFDancer () {
        return this.$store.getters.currentIABFDancer
      },
      dancerId () {
        return this.currentIABFDancer ? this.currentIABFDancer.id : null
      },
      packageKey () {
        return packageKeyFromRoom(this.room)
      },
      packageName () {
        return packageNameFromRoom(this.room)
      },
      roomAmenities () {
        const d = this.currentIABFDancer || {}
        const wants = this.wantsHairdryer != null
          ? this.wantsHairdryer
          : (d.wantsHairdryer === true ? true : (d.wantsHairdryer === false ? false : null))
        return roomAmenitiesForRoom(this.room, {
          dietaryEntered: d.dietaryRestrictionsEntered === true,
          wantsHairdryer: wants,
          coffeeMakerStock: this.coffeeMakerStock.length
            ? this.coffeeMakerStock
            : d.coffeeMakerStock,
          coffee: d.coffee
        })
      },
      showCoffeeMakerSummary () {
        return this.coffeeMakerEntered && !this.coffeeMakerEditing
      },
      coffeeMakerSummaryLine () {
        return coffeeMakerSummary(this.coffeeMakerStock, this.currentIABFDancer && this.currentIABFDancer.coffee)
      },
      lodgingHeadline () {
        if (!this.room) {
          return 'Lodging details'
        }
        return lodgingLabelFromRoom(this.room)
      },
      lodgingAccordionTitle () {
        if (this.isOffsite) {
          return 'Check out Offsite Hotel Recommendations'
        }
        return 'Customize your Room'
      },
      lodgingAccordionHint () {
        if (this.isOnsiteStay) {
          if (this.assignedRoom) {
            return 'You’re in room ' + this.assignedRoom + '. Hairdryer, coffee, and extra nights if you need them.'
          }
          return 'Hairdryer, coffee, and extra nights if you need them.'
        }
        if (this.isOffsite) {
          return 'Hotels we like, shuttle, and parking.'
        }
        return 'Your stay details for festival week.'
      },
      tripAccordionHint () {
        if (this.travelDetailsEntered && this.arrivalType) {
          return 'How you’re getting here, and how you’re heading home.'
        }
        return 'Tell us how you’re getting here — plane or car, dates, and flight numbers.'
      },
      assignedRoom () {
        return assignedRoomNumber(this.currentIABFDancer)
      },
      lodgingSummary () {
        if (!this.room) {
          return 'We’re still matching your room type to your registration.'
        }
        const roomBit = this.assignedRoom ? ' You’re in room ' + this.assignedRoom + '.' : ''
        if (this.isOnsiteRetreat) {
          if (this.room === 'Standard Dorm') {
            return 'You’re in your own onsite dorm room at the Artist’s Retreat for the festival week.' + roomBit
          }
          if (this.room === 'ADA Dorm') {
            return 'You’re in a larger onsite dorm room at the Artist’s Retreat for the festival week.' + roomBit
          }
          if (this.room === 'Cottage') {
            return 'You’re in an onsite cottage at the Artist’s Retreat for the festival week.' + roomBit
          }
          return 'You’re staying onsite at the Artist’s Retreat for the festival week.' + roomBit
        }
        if (this.isSharingRoom) {
          const shareName = String(
            (this.currentIABFDancer && (this.currentIABFDancer.roommateName || this.currentIABFDancer.primaryRoommateName)) ||
            ''
          ).trim()
          const shareLine = shareName
            ? `You’re sharing an onsite room with ${shareName}.`
            : 'You’re sharing an onsite room at the Artist’s Retreat — the room is confirmed under your roommate’s name.'
          return shareLine + roomBit
        }
        if (this.isOffsite) {
          return this.hasShuttle
            ? 'You’re staying offsite and booking your own lodging. A shuttle to the venue is included with your registration.'
            : 'You’re staying offsite and booking your own lodging. Plan your own transport to the venue (complimentary parking onsite).'
        }
        return lodgingLabelFromRoom(this.room)
      },
      isOnsiteRetreat () {
        return this.packageKey === 'retreat'
      },
      isSharingRoom () {
        return this.room === 'Staying with another dancer'
      },
      /** Onsite for lodging guidance (own room or sharing). */
      isOnsiteStay () {
        return this.isOnsiteRetreat || this.isSharingRoom
      },
      isOffsite () {
        if (!this.room) {
          return false
        }
        return this.room === 'Staying offsite' || String(this.room).indexOf('offsite') !== -1
      },
      hasShuttle () {
        // Legacy "Staying offsite" (no parenthetical) included shuttle.
        return this.room === 'Staying offsite (shuttle)' || this.room === 'Staying offsite'
      },
      festival () {
        return festivalWeek(IABF_DANCER_YEAR)
      },
      extraNights () {
        return extraNightIsoWindows(IABF_DANCER_YEAR)
      }
    },
    created () {
      this.$store.commit('setLayout', 'iabf-layout')
      if (this.dancerId) {
        this.populateFromDancer()
      }
    },
    watch: {
      currentIABFDancer (val) {
        if (val && val.id) {
          this.populateFromDancer()
        }
      }
    },
    methods: {
      formatTripDate (iso) {
        if (!iso) {
          return ''
        }
        const parts = String(iso).slice(0, 10).split('-')
        if (parts.length !== 3) {
          return iso
        }
        const months = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ]
        const y = parseInt(parts[0], 10)
        const m = parseInt(parts[1], 10)
        const day = parseInt(parts[2], 10)
        if (!m || !day) {
          return iso
        }
        return months[m - 1] + ' ' + day + ', ' + y
      },
      normalizeExtraNightStatus (raw) {
        if (!raw || raw === true || raw === false) {
          return ''
        }
        const s = String(raw).toLowerCase().trim()
        if (s === 'requested' || s === 'confirmed' || s === 'paid') {
          return s
        }
        // Legacy truthy strings
        if (s && s !== 'false' && s !== 'null') {
          return 'requested'
        }
        return ''
      },
      nightStatusLabel (status) {
        if (status === 'requested') return 'Requested'
        if (status === 'confirmed') return 'Confirmed'
        if (status === 'paid') return 'Paid'
        return status
      },
      toggleExtraNightPick (key, checked) {
        // Only for nights not already on file
        if (this.normalizeExtraNightStatus(this[key])) {
          return
        }
        this[key] = !!checked
        this.extraNightsError = ''
        this.extraNightsSuccess = ''
      },
      async saveHairdryerPreference (wants) {
        if (!this.dancerId || this.hairdryerSaving) {
          return
        }
        this.hairdryerSaving = true
        this.hairdryerError = ''
        this.hairdryerSuccess = ''
        try {
          await this.$store.dispatch('updateDancerHairdryerPreference', {
            dancerId: this.dancerId,
            wantsHairdryer: !!wants
          })
          this.wantsHairdryer = !!wants
          this.hairdryerSuccess = wants
            ? 'Got it — we’ll put a hairdryer in your room.'
            : 'Got it — no hairdryer needed.'
        } catch (e) {
          console.error(e)
          this.hairdryerError = saveErrorMessage(e) || 'Could not save. Please try again.'
        }
        this.hairdryerSaving = false
      },
      openCarpoolStep () {
        this.showCarpoolInfo = true
        this.$nextTick(() => {
          const el = document.getElementById('carpool')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      },
      async saveCoffeeMakerStock () {
        if (!this.dancerId || this.coffeeMakerSaving) {
          return
        }
        if (!this.coffeeMakerStock.length) {
          this.coffeeMakerError = 'Pick what you’d like in your coffee maker — or None if you’re set.'
          return
        }
        this.coffeeMakerSaving = true
        this.coffeeMakerError = ''
        this.coffeeMakerSuccess = ''
        try {
          await this.$store.dispatch('updateDancerCoffeeMakerStock', {
            dancerId: this.dancerId,
            coffeeMakerStock: this.coffeeMakerStock
          })
          this.coffeeMakerEntered = true
          this.coffeeMakerEditing = false
          this.coffeeMakerSuccess = 'Got it — we’ll stock your coffee maker.'
        } catch (e) {
          console.error(e)
          this.coffeeMakerError = saveErrorMessage(e) || 'Could not save. Please try again.'
        }
        this.coffeeMakerSaving = false
      },
      clearTripForm () {
        // Year-scoped: each festival starts with no trip on file
        this.arrivalType = ''
        this.arrivalAirport = ''
        this.arrivalAirportOther = ''
        this.arrivalFlightNumber = ''
        this.arrivalDate = this.festival.arrivalIso
        this.arrivalTime = ''
        this.departureType = ''
        this.departureAirport = ''
        this.departureAirportOther = ''
        this.departureFlightNumber = ''
        this.departureDate = this.festival.departureIso
        this.departureTime = ''
        this.extraNight1 = false
        this.extraNight2 = false
        this.extraNight3 = false
        this.extraNight4 = false
        this.extraNight5 = false
        this.extraNight6 = false
        this.extraNightPayment = ''
        this.extraNightPaymentMethod = ''
        this.extraNightsTotal = 0
        this.extraNightRemainingBalance = 0
        this.arrivalSharedCar = false
        this.departureSharedCar = false
        this.travelDetailsId = null
        this.travelDetailsEntered = false
      },
      travelDocHasContent (td) {
        if (!td) {
          return false
        }
        if (td.arrivalAirport || td.arrivalType || td.departureAirport || td.departureType) {
          return true
        }
        // Extra-nights-only docs (request before full trip form) still count
        for (const key of this.extraNightKeys) {
          if (this.normalizeExtraNightStatus(td[key]) || td[key] === true) {
            return true
          }
        }
        return false
      },
      async populateFromDancer () {
        if (this.$store.state.masqueradeDancerId && !this._masqueradeRefreshed) {
          this._masqueradeRefreshed = true
          try {
            await this.$store.dispatch('refreshEffectiveDancer')
          } catch (e) {
            console.warn('Masquerade dancer refresh failed', e)
          }
        }
        const d = this.currentIABFDancer
        if (!d || !d.id) {
          return
        }
        // Guard against concurrent loads (watcher + created) wiping a good result
        const loadGen = (this._travelLoadGen = (this._travelLoadGen || 0) + 1)
        this.firstName = d.firstName
        this.lastName = d.lastName
        this.room = d.room
        this.primaryRoomId = d.primaryRoomId
        if (d.wantsHairdryer === true) {
          this.wantsHairdryer = true
        } else if (d.wantsHairdryer === false) {
          this.wantsHairdryer = false
        } else {
          this.wantsHairdryer = null
        }
        if (!this.coffeeMakerEditing) {
          if (Array.isArray(d.coffeeMakerStock) && d.coffeeMakerStock.length) {
            this.coffeeMakerStock = [...d.coffeeMakerStock]
          } else {
            this.coffeeMakerStock = []
          }
          this.coffeeMakerEntered = hasCoffeeMakerPicks(d.coffeeMakerStock, d.coffee)
        }

        // Travel is year-scoped on this year's enrollment only.
        const entered = d.travelDetailsEntered === true
        const travelId = d.travelDetailsId || null
        this.travelDetailsEntered = entered
        if (travelId) {
          this.travelDetailsId = travelId
        }

        // Always try to load year-scoped travel details — extra nights may exist
        // even when the full trip form has not been submitted yet.
        let travelDetails = null
        try {
          if (travelId) {
            travelDetails = await this.$store.dispatch('loadIABFTravelDetails', travelId)
          }
          if (!this.travelDocHasContent(travelDetails)) {
            const fallback = await this.$store.dispatch('loadTravelDetailsByPersonId', d.id)
            if (this.travelDocHasContent(fallback)) {
              travelDetails = fallback
              if (fallback.id && fallback.id !== this.travelDetailsId) {
                this.travelDetailsId = fallback.id
                this.$store.dispatch('updateDancerTravelDetailsId', {
                  dancerId: d.id,
                  travelDetailsId: fallback.id
                })
              }
            }
          }
        } catch (e) {
          console.warn('populateFromDancer travel load failed', e)
        }

        // Stale async response — a newer populate started after us
        if (loadGen !== this._travelLoadGen) {
          return
        }

        if (this.travelDocHasContent(travelDetails)) {
          this.setTravelDetails(travelDetails)
          // Full trip form only when arrival/departure was actually entered
          const hasTrip = !!(
            travelDetails.arrivalType ||
            travelDetails.arrivalAirport ||
            travelDetails.departureType ||
            travelDetails.departureAirport
          )
          this.travelDetailsEntered = entered || hasTrip
          if (hasTrip && entered !== true && this.dancerId) {
            this.$store.dispatch('markTravelDetailsEntered', {
              dancerId: this.dancerId,
              travelDetailsId: this.travelDetailsId || (travelDetails && travelDetails.id) || null,
              arrivalType: travelDetails.arrivalType || this.arrivalType || '',
              departureType: travelDetails.departureType || this.departureType || ''
            }).catch((e) => {
              console.warn('Could not mark trip details entered', e)
            })
          }
        } else {
          // Keep travelDetailsId if dancer already has a link (empty shell doc)
          const keepId = this.travelDetailsId
          this.clearTripForm()
          if (keepId) {
            this.travelDetailsId = keepId
          }
        }
      },
      setTravelDetails (travelDetails) {
        // Only overwrite trip fields when present (extra-nights-only docs keep defaults)
        if (travelDetails.arrivalType) {
          this.arrivalType = travelDetails.arrivalType
        }
        if (travelDetails.arrivalAirport) {
          this.arrivalAirport = travelDetails.arrivalAirport
        }
        if (travelDetails.arrivalFlightNumber != null) {
          this.arrivalFlightNumber = travelDetails.arrivalFlightNumber
        }
        if (travelDetails.arrivalDate) {
          this.arrivalDate = travelDetails.arrivalDate
        }
        if (travelDetails.arrivalTime) {
          this.arrivalTime = travelDetails.arrivalTime
        }
        if (travelDetails.departureType) {
          this.departureType = travelDetails.departureType
        }
        if (travelDetails.departureAirport) {
          this.departureAirport = travelDetails.departureAirport
        }
        if (travelDetails.departureFlightNumber != null) {
          this.departureFlightNumber = travelDetails.departureFlightNumber
        }
        if (travelDetails.departureDate) {
          this.departureDate = travelDetails.departureDate
        }
        if (travelDetails.departureTime) {
          this.departureTime = travelDetails.departureTime
        }
        // Keep status strings (requested / confirmed / paid) so the dancer sees them
        this.extraNight1 = travelDetails.extraNight1 || false
        this.extraNight2 = travelDetails.extraNight2 || false
        this.extraNight3 = travelDetails.extraNight3 || false
        this.extraNight4 = travelDetails.extraNight4 || false
        this.extraNight5 = travelDetails.extraNight5 || false
        this.extraNight6 = travelDetails.extraNight6 || false
        this.arrivalSharedCar = travelDetails.arrivalSharedCar
        this.departureSharedCar = travelDetails.departureSharedCar
        this.checkIn = travelDetails.checkIn || this.checkIn
        this.checkOut = travelDetails.checkOut || this.checkOut
        this.extraNightsTotal = travelDetails.extraNightsTotal || 0
        this.extraNightRemainingBalance = travelDetails.extraNightRemainingBalance || 0
        this.extraNightPaymentMethod = travelDetails.extraNightPaymentMethod || ''
        this.extraNightInvoiceLink = travelDetails.extraNightInvoiceLink || ''
        this.extraNightPayment = travelDetails.extraNightPaymentMethod || this.extraNightPayment || ''
        if (travelDetails.id) {
          this.travelDetailsId = travelDetails.id
        }
        // If nights are already on file, expand lodging so status is visible
        this.$nextTick(() => {
          if (this.hasAnyExtraNightOnFile) {
            this.showLodgingInfo = true
          }
        })
      },
      validateTravelDetails () {
        if (!this.dancerId) {
          return 'Your registration is still loading. Wait a moment and try again.'
        }
        if (!this.arrivalType) {
          return 'Please choose how you are arriving (Plane or Car).'
        }
        if (this.arrivalType === 'Plane') {
          if (!this.arrivalAirport) {
            return 'Please select your arrival airport.'
          }
          if (this.arrivalAirport === 'Other' && !this.arrivalAirportOther) {
            return 'Please enter your arrival airport.'
          }
          if (!this.arrivalFlightNumber) {
            return 'Please enter your arrival flight number.'
          }
        }
        if (!this.arrivalDate) {
          return 'Please select your arrival date.'
        }
        if (!this.arrivalTime) {
          return 'Please enter your arrival time.'
        }
        if (!this.departureType) {
          return 'Please choose how you are departing (Plane or Car).'
        }
        if (this.departureType === 'Plane') {
          if (!this.departureAirport) {
            return 'Please select your departure airport.'
          }
          if (this.departureAirport === 'Other' && !this.departureAirportOther) {
            return 'Please enter your departure airport.'
          }
          if (!this.departureFlightNumber) {
            return 'Please enter your departure flight number.'
          }
        }
        if (!this.departureDate) {
          return 'Please select your departure date.'
        }
        if (!this.departureTime) {
          return 'Please enter your departure time.'
        }
        return null
      },
      buildExtraNightsPayload () {
        // Preserve existing statuses; mark new picks as requested
        const nights = {}
        for (const key of this.extraNightKeys) {
          const existing = this.normalizeExtraNightStatus(this[key])
          if (existing) {
            nights[key] = existing
          } else if (this[key] === true) {
            nights[key] = 'requested'
          }
          // Omit unset nights so merge write does not clear other fields
        }
        const allForDates = { ...nights }
        return {
          travelDetailsId: this.travelDetailsId || null,
          personId: this.dancerId,
          personType: this.isFaculty ? 'faculty' : 'dancer',
          ...nights,
          extraNightsTotal: this.roomTotal,
          extraNightPaymentMethod: this.extraNightPayment || this.extraNightPaymentMethod || '',
          checkIn: computeCheckIn(allForDates),
          checkOut: computeCheckOut(allForDates),
          dancerName: `${this.firstName || ''} ${this.lastName || ''}`.trim(),
          dancerEmail: (this.currentIABFDancer && this.currentIABFDancer.email) || '',
          room: this.room || '',
          selectedNights: this.selectedExtraNightLabels,
          newlyRequestedNights: this.extraNightRows.filter(n => n.picking).map(n => n.label),
          changeType: 'Extra nights request'
        }
      },
      async submitExtraNightsRequest () {
        this.extraNightsError = ''
        this.extraNightsSuccess = ''
        if (!this.dancerId) {
          this.extraNightsError = 'Your registration is still loading. Wait a moment and try again.'
          return
        }
        if (this.newPickCount <= 0) {
          this.extraNightsError = 'Select at least one new night to request.'
          return
        }
        if (!this.extraNightPayment && !this.extraNightPaymentMethod) {
          this.extraNightsError = 'Please choose how you would like to pay for extra nights.'
          return
        }
        this.extraNightsSaving = true
        try {
          const result = await this.$store.dispatch(
            'requestExtraNights',
            this.buildExtraNightsPayload()
          )
          // Apply full saved doc so badges match Firestore (incl. prior nights)
          if (result) {
            this.setTravelDetails(result)
            if (result.id) {
              this.travelDetailsId = result.id
            }
          } else {
            for (const key of this.extraNightKeys) {
              if (this[key] === true) {
                this[key] = 'requested'
              }
            }
          }
          this.extraNightsTotal = this.roomTotal
          this.extraNightPaymentMethod = this.extraNightPayment || this.extraNightPaymentMethod
          this.extraNightsSuccess =
            'Request sent — status is “Requested” until we confirm with the venue.'
          // Auto-expand lodging so they see the badges
          this.showLodgingInfo = true
        } catch (err) {
          this.extraNightsError = saveErrorMessage(err, 'Could not send your extra-nights request.')
        }
        this.extraNightsSaving = false
      },
      submitTravelDetails () {
        this.travelError = ''
        this.travelSuccess = ''
        const validationError = this.validateTravelDetails()
        if (validationError) {
          this.travelError = validationError
          return
        }
        var travelDetails = {
          travelDetailsId: this.travelDetailsId || null,
          personId: this.dancerId,
          personType: this.isFaculty ? 'faculty' : 'dancer',
          arrivalType: this.arrivalType,
          arrivalFlightNumber: this.arrivalFlightNumber,
          arrivalDate: this.arrivalDate,
          arrivalTime: this.arrivalTime,
          departureType: this.departureType,
          departureFlightNumber: this.departureFlightNumber,
          departureDate: this.departureDate,
          departureTime: this.departureTime,
          checkIn: this.defaultCheckIn,
          checkOut: this.defaultCheckOut
        }
        if (this.departureAirport === 'Other') {
          travelDetails['departureAirport'] = this.departureAirportOther
        } else {
          travelDetails['departureAirport'] = this.departureAirport
        }
        if (this.arrivalAirport === 'Other') {
          travelDetails['arrivalAirport'] = this.arrivalAirportOther
        } else {
          travelDetails['arrivalAirport'] = this.arrivalAirport
        }
        // Preserve extra-night statuses when saving trip (don't downgrade confirmed → requested)
        for (const key of this.extraNightKeys) {
          const existing = this.normalizeExtraNightStatus(this[key])
          if (existing) {
            travelDetails[key] = existing
          } else if (this[key] === true) {
            travelDetails[key] = 'requested'
          }
        }
        if (this.roomTotal > 0) {
          travelDetails['extraNightsTotal'] = this.roomTotal
          // Earliest requested before-night sets check-in; check-out is the
          // morning after the latest requested after-night (e.g. an extra night
          // on July 11 means checking out July 12). Shared with the admin view
          // and rooming-list export so the dates always agree.
          const requestedNights = {}
          for (const key of this.extraNightKeys) {
            requestedNights[key] = travelDetails[key] || this[key]
          }
          travelDetails['checkIn'] = computeCheckIn(requestedNights)
          travelDetails['checkOut'] = computeCheckOut(requestedNights)
          travelDetails['extraNightPaymentMethod'] =
            this.extraNightPayment || this.extraNightPaymentMethod || ''
        }
        if (this.arrivalSharedCar) {
          travelDetails['arrivalSharedCar'] = 'requested'
        }
        if (this.departureSharedCar) {
          travelDetails['departureSharedCar'] = 'requested'
        }
        this.travelSaving = true
        this.$store.dispatch('createIABFTravelDetails', travelDetails).then(() => {
          this.travelDetailsEntered = true
          var loaded = this.$store.getters.loadedTravelDetails
          this.setTravelDetails(loaded)
          if (loaded && loaded.id) {
            this.travelDetailsId = loaded.id
          }
          this.travelSuccess = 'All set — thanks for the trip details!'
          this.travelSaving = false
        }).catch((err) => {
          this.travelError = saveErrorMessage(err)
          this.travelSaving = false
        })
      }
    }
  }
</script>

<style scoped>
.lodging-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}

.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  transition: color 0.15s;
}

.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}

h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 16px 0 8px 0;
}

.page-subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 28px;
  line-height: 1.5;
}

.hairdryer-block {
  margin: 0 0 8px;
  padding: 0 0 18px;
}
.hairdryer-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}
.hairdryer-choice {
  min-width: 120px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1.5px solid #d5d1db;
  background: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.hairdryer-choice:hover:not(:disabled) {
  border-color: var(--iabf-primary, #8a957d);
}
.hairdryer-choice.is-selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.16);
}
.hairdryer-choice:disabled {
  opacity: 0.65;
  cursor: wait;
}
.coffee-block {
  margin: 0 0 8px;
  padding: 16px 0 18px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.coffee-summary {
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
  font-weight: 600;
}
.coffee-update {
  margin-top: 4px;
}
.coffee-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 4px;
}

.lodging-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.your-lodging {
  margin: 0;
  padding: 0;
  overflow: hidden;
}
.your-lodging-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 18px 20px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.your-lodging-header-text {
  min-width: 0;
  flex: 1;
}
.your-lodging-chevron {
  flex-shrink: 0;
  margin-top: 4px;
  color: var(--iabf-ink-faint, #8a8792) !important;
}
.your-lodging-kicker {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--iabf-primary, #8a957d);
}
.your-lodging-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0;
  border: none;
  padding: 0;
}
.your-lodging-collapsed-hint {
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.lodging-pending-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: rgba(196, 146, 58, 0.2);
  color: #8a6a28;
  vertical-align: middle;
}
.lodging-pending-pill--confirmed {
  background: rgba(76, 140, 106, 0.18);
  color: #2f6b4f;
}
.lodging-pending-pill--inline {
  margin-left: 0;
  margin-bottom: 6px;
}
.extra-nights-fact-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}
.extra-nights-fact-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
  font-size: 14px;
}
.your-lodging-body {
  padding: 0 20px 20px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  padding-top: 16px;
}
.your-lodging-lede {
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.your-lodging-lede a,
.lodging-package-link,
.extra-nights-fine a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  text-decoration: none;
}
.your-lodging-lede a:hover,
.lodging-package-link:hover,
.extra-nights-fine a:hover {
  text-decoration: underline;
}
.lodging-facts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 18px;
  padding: 14px 16px;
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 12px;
}
.lodging-fact {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lodging-fact-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
}
.lodging-fact-value {
  font-size: 15px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.lodging-fact-value--stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.lodging-package-link {
  margin-left: 8px;
  font-size: 14px;
}
.lodging-amenities {
  margin: 0 0 18px;
  padding: 14px 16px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  background: #fff;
}
.lodging-amenities-title {
  margin: 0 0 8px;
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.lodging-amenities-list {
  margin: 0;
  padding-left: 1.2em;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.lodging-amenities-list li {
  margin-bottom: 4px;
}
.amenity-link {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
  text-decoration: underline;
}
.amenity-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
.lodging-callout {
  margin: 0 0 16px;
  padding: 14px 16px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  background: #fff;
}
.lodging-callout--soft {
  background: rgba(138, 149, 125, 0.08);
  border-color: rgba(138, 149, 125, 0.25);
}
.lodging-callout strong {
  display: block;
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 6px;
}
.lodging-callout p {
  margin: 0 0 8px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.lodging-callout p:last-child {
  margin-bottom: 0;
}
.hotel-list {
  margin: 8px 0 0;
  padding-left: 1.2em;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}
.hotel-list li {
  margin-bottom: 6px;
}
.extra-nights-block {
  margin-top: 4px;
  padding-top: 16px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.extra-nights-heading {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 18px;
  margin: 0 0 8px;
  color: var(--iabf-ink, #1c1b1f);
  border: none;
  padding: 0;
}
.trip-subheading {
  margin-top: 22px;
}
.extra-nights-copy {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
}
.extra-nights-status-banner {
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(196, 146, 58, 0.12);
  border: 1px solid rgba(196, 146, 58, 0.35);
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink, #1c1b1f);
}
.extra-nights-status-banner strong {
  display: block;
  margin-bottom: 4px;
  color: #8a6a28;
}
.extra-nights-status-banner p {
  margin: 0;
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.extra-night-list {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.extra-night-row {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  background: #fff;
  padding: 10px 12px;
}
.extra-night-row.is-requested {
  border-color: rgba(196, 146, 58, 0.45);
  background: rgba(196, 146, 58, 0.08);
}
.extra-night-row.is-confirmed {
  border-color: rgba(95, 122, 85, 0.4);
  background: rgba(95, 122, 85, 0.08);
}
.extra-night-pick {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin: 0;
  font: inherit;
}
.extra-night-pick input {
  width: 18px;
  height: 18px;
  accent-color: var(--iabf-primary, #8a957d);
  flex-shrink: 0;
}
.extra-night-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.extra-night-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
}
.extra-night-hint {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  margin-left: auto;
}
.extra-night-badge {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
}
.badge-requested {
  background: rgba(196, 146, 58, 0.2);
  color: #8a6a28;
}
.badge-confirmed,
.badge-paid {
  background: rgba(95, 122, 85, 0.18);
  color: var(--iabf-success, #5f7a55);
}
.extra-nights-pay {
  margin-top: 8px;
  padding: 14px 16px;
  background: rgba(138, 149, 125, 0.1);
  border-radius: 12px;
}
.extra-nights-total {
  margin: 0 0 8px;
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}
.extra-night-radios {
  margin: 0 0 8px !important;
  padding: 0 !important;
}
.extra-nights-fine {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--iabf-ink-faint, #8a8792);
}
.meals-in-total {
  margin: 12px 0;
}
.extra-nights-save {
  margin-top: 4px;
}
.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 14px;
  margin: 0 0 10px;
  line-height: 1.4;
}
.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
  line-height: 1.4;
}

.trip-question {
  font-size: 16px;
  font-weight: 500;
  color: var(--iabf-ink, #1c1b1f);
  margin: 18px 0 10px;
}

.trip-pills {
  margin-bottom: 8px;
}

.trip-summary {
  margin-bottom: 8px;
}

.trip-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 16px 18px;
  margin: 0 0 12px;
}

.trip-card-kicker {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 6px;
}

.trip-card-body {
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0;
  line-height: 1.5;
}

.trip-card-note {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
}

.stay-inline-note {
  margin-top: 16px !important;
  font-size: 15px !important;
  color: var(--iabf-ink-muted, #5c5a63) !important;
}

.stay-inline-note a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 700;
  text-decoration: none;
}

.stay-inline-note a:hover {
  text-decoration: underline;
}

.carpool-on-travel {
  margin-top: 40px;
  padding-top: 8px;
}

.carpool-on-travel-lede {
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 16px;
  line-height: 1.5;
}

.carpool-jump {
  display: inline-block;
  text-decoration: none;
  margin-top: 4px;
}

h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -.01em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 40px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 20px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 28px 0 10px 0;
}

p, li {
  font-family: var(--iabf-font-body, inherit);
  font-weight: 400;
  font-size: 17px;
  line-height: 1.8;
  color: var(--iabf-ink-muted, #5c5a63);
}

ul {
  padding-left: 20px;
  margin: 4px 0 12px 0;
}

li {
  margin-bottom: 4px;
}

a {
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: underline;
}

.carpool-cta {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 16px 20px;
  margin: 16px 0 8px;
}

.carpool-cta p {
  margin: 0 0 12px;
  color: var(--iabf-ink, #1c1b1f);
}

.info-box {
  background: #ffffff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 20px 22px;
  margin: 20px 0;
  color: var(--iabf-ink, #1c1b1f);
}

.info-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.info-box h3 {
  margin-top: 0;
  margin-bottom: 0;
}

.form-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 15px;
  margin: 12px 0 8px;
  line-height: 1.4;
}

.form-success {
  color: var(--iabf-success, #5f7a55);
  font-size: 15px;
  margin: 12px 0 8px;
  line-height: 1.4;
}
</style>
